import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  RefreshCw,
  Sparkles,
  Search,
  Filter,
  ArrowUpDown,
  ArrowDownUp,
  CheckCheck,
  CheckCircle2,
  ChevronRight,
  Clipboard,
  Loader2,
  X,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, unwrap } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { usePermissions } from '@/hooks/usePermissions';
import StatCard from '@/components/match/board/StatCard';
import { StatusPill } from '@/components/match/board/StatusPill';
import { ConfidenceBar } from '@/components/match/board/ConfidenceBar';
import { FlagChip } from '@/components/match/board/FlagChip';
import RowDetailDrawer from '@/components/match/board/RowDetailDrawer';
import { SanitizeStatsBadge } from '@/components/match/SessionsTab';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BASE = `${API_URL}/api/match`;

// Statuses a row must be in to be hand-selected for "Approve selected".
// Mirrors the backend row_ids eligibility filter
// (_BULK_APPROVE_ELIGIBLE_STATUSES in backend/routes/match_row.py) EXACTLY —
// a checkbox that lets you select an ineligible row would just get silently
// skipped server-side, so we disable it here instead. Note this is
// status-only: the selection path ignores confidence entirely (unlike the
// legacy "≥ 95%" sweep), so a low-confidence needs_review row is selectable.
const SELECTABLE_STATUSES = ['ready_to_draft', 'needs_review'];

function isRowSelectable(row) {
  return SELECTABLE_STATUSES.includes(row.status);
}

const STAT_CARDS = [
  { key: 'ready_to_draft', label: 'READY TO DRAFT', accent: 'warn' },
  { key: 'needs_review',   label: 'NEEDS REVIEW',   accent: 'warn' },
  { key: 'approved',       label: 'APPROVED',        accent: 'success' },
  { key: 'rejected',       label: 'REJECTED',        accent: 'danger' },
  { key: 'escalated',      label: 'ESCALATED',       accent: 'info' },
];

// Flag filter definitions — labels map raw flag keys to human labels.
// Backend canonical flag keys come from match-main/backend/utils.py — do NOT
// invent friendlier-looking names; the chip won't filter if it doesn't match.
const FLAG_FILTER_DEFS = [
  { key: 'name_variation',       label: 'Name variation' },
  { key: 'address_discrepancy',  label: 'Address mismatch' },
  { key: 'new_customer',         label: 'New customer' },
  { key: 'new_vs_existing',      label: 'New vs existing' },
];

// Rows rendered per board page. Counters/eligibility/search span the full
// set; only the DOM is paged (1,500 mounted row components was the load-test
// render bottleneck).
const BOARD_PAGE_SIZE = 100;

// Snapshot source badge classes
const SNAPSHOT_SOURCE_CLS = {
  live_api: 'bg-boss-status-success-bg text-boss-status-success border-[var(--boss-success-border)]',
  upload:   'bg-boss-status-info-bg text-boss-status-info border-[var(--boss-info-border)]',
};

const SNAPSHOT_SOURCE_LABEL = {
  live_api: 'Live fetch',
  upload:   'Uploaded snapshots',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract the flat display fields from the nested row shape.
 * The row data shape has output.top_level / output.order_details etc.
 * The existing BoardTab also stored some flat fields directly on the row
 * (customer_name, advisor_company) — we normalise both shapes here.
 */
function flattenRow(row) {
  const audit = row.audit_metadata ?? {};
  const out = row.output ?? {};
  const top = out.top_level ?? {};
  const cust = top.customer ?? {};
  const od = out.order_details ?? {};
  const loc = top.location ?? {};

  const customerName =
    cust.name ||
    row.customer_name ||
    row.source_row?.['Account Name'] ||
    row.source_row?.['Customer Name'] ||
    '';

  const advisorCompany =
    top.advisor_company ||
    row.advisor_company ||
    row.source_row?.['Agency'] ||
    row.source_row?.['Advisor'] ||
    '';

  const cityState = (loc.city || loc.state)
    ? `${loc.city || ''}, ${loc.state || ''}`.trim().replace(/^,\s*/, '')
    : (row.city_state || '');

  const mrc = od.mrgmv ?? row.mrc ?? null;
  const contractType = od.contract_type ?? row.contract_type ?? '';
  const provider = od.provider ?? row.provider ?? '';
  // Canonical field is audit_metadata.confidence_score. The live n8n
  // pipeline emits 0-100 ints (webhook gate clamps to that scale); legacy
  // fixture rows carried 0-1 floats. Normalize to 0-100 here so eligibility,
  // sorting, and display all agree (same <=1 heuristic as ConfidenceBar).
  const rawConfidence = audit.confidence_score ?? row.confidence_score ?? 0;
  const confidenceScore = rawConfidence <= 1 ? rawConfidence * 100 : rawConfidence;
  const flags = audit.flags ?? row.flags ?? [];

  // Provider Sales order id — preferred source is top-level post-push field;
  // fall back to the match-hint nested in output (present before push completes).
  const psOrderId =
    row.appdirect_order_id ||
    row.output?.order_details?.appdirect_order_id ||
    null;

  return {
    customerName,
    advisorCompany,
    cityState,
    mrc,
    contractType,
    provider,
    confidenceScore,
    flags,
    psOrderId,
  };
}

function fmtMoney(v) {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SortableTh({ label, sortKey: k, activeKey, dir, onSort, className = '' }) {
  const active = activeKey === k;
  return (
    <th
      className={[
        'text-left px-3 py-2 text-xs font-medium text-boss-text-secondary whitespace-nowrap',
        className,
      ].join(' ')}
    >
      <button
        onClick={() => onSort(k)}
        className={[
          'inline-flex items-center gap-1 hover:text-boss-text transition-colors',
          active ? 'text-boss-text' : '',
        ].join(' ')}
        data-testid={`board-sort-${k}`}
      >
        {label}
        {active ? (
          <ArrowDownUp className="h-3 w-3 opacity-70" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </th>
  );
}

function SkeletonRows() {
  return [0, 1, 2, 3, 4].map((i) => (
    <tr key={i} className="border-b border-boss-border">
      {/* leading select cell */}
      <td className="px-3 py-3 w-8" />
      {[80, 180, 120, 100, 60, 60, 110, 80, 80].map((w, j) => (
        <td key={j} className="px-3 py-3">
          <div
            className="h-3 rounded bg-boss-surface-elevated animate-pulse"
            style={{ width: w }}
          />
        </td>
      ))}
      <td className="px-3 py-3 w-8" />
    </tr>
  ));
}

function EmptyState({ hasRows, onSimulate, processing, timedOut }) {
  if (processing && !timedOut) {
    return (
      <tr>
        <td colSpan={11}>
          <div className="py-12 text-center space-y-2">
            <div className="flex items-center justify-center gap-2 text-boss-status-info text-sm font-medium">
              <Loader2 className="h-4 w-4 animate-spin" />
              Matching in progress — rows will appear here as they arrive…
            </div>
            <p className="text-xs text-boss-text-secondary">
              This can take a minute or two depending on the report size.
            </p>
          </div>
        </td>
      </tr>
    );
  }
  if (processing && timedOut) {
    return (
      <tr>
        <td colSpan={11}>
          <div className="py-12 text-center space-y-2">
            <div className="flex items-center justify-center gap-2 text-boss-status-warn text-sm font-medium">
              <AlertTriangle className="h-4 w-4" />
              Still matching — taking longer than expected
            </div>
            <p className="text-xs text-boss-text-secondary">
              The pipeline may still be running. Refresh manually to check.
            </p>
          </div>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td colSpan={11}>
        <div className="py-12 text-center space-y-2">
          <p className="text-sm font-medium text-boss-text">
            {hasRows ? 'No rows match your filters' : 'No rows yet'}
          </p>
          <p className="text-xs text-boss-text-secondary">
            {hasRows
              ? 'Adjust the search or clear the filter chips.'
              : "Rows will appear here once n8n processes the session. Use 'Re-run simulate' to retrigger."}
          </p>
          {!hasRows && onSimulate && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSimulate}
              className="mt-2"
              data-testid="empty-board-simulate-btn"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1" />
              Re-run simulate
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function BoardRow({ row, selected, checked, selectable, onToggleSelect, onClick }) {
  const flat = flattenRow(row);

  return (
    <tr
      className={[
        'border-b border-boss-border cursor-pointer transition-colors group',
        selected
          ? 'bg-boss-surface-elevated'
          : 'hover:bg-boss-surface-elevated/60',
      ].join(' ')}
      onClick={onClick}
      data-testid={`board-row-${row.row_id}`}
    >
      {/* Selection checkbox — stopPropagation so it never opens the drawer */}
      <td className="px-3 py-2.5 w-8" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-boss-border bg-boss-surface accent-[var(--boss-accent)] cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
          checked={checked}
          disabled={!selectable}
          onChange={() => onToggleSelect(row.row_id)}
          title={selectable ? 'Select row' : 'Row is not in an approvable status'}
          data-testid={`board-row-select-${row.row_id}`}
        />
      </td>

      {/* PS Order — primary identifier, shown first */}
      <td className="px-3 py-2.5 w-[110px]" onClick={(e) => e.stopPropagation()}>
        {flat.psOrderId ? (
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(String(flat.psOrderId)).catch(() => null);
              toast.success('PS Order ID copied');
            }}
            className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-boss-text hover:text-boss-accent transition-colors"
            title={`Copy PS Order ID: ${flat.psOrderId}`}
            data-testid={`board-row-ps-order-${row.row_id}`}
          >
            <Clipboard className="h-3 w-3 flex-shrink-0" />
            {flat.psOrderId}
          </button>
        ) : (
          <span className="font-mono text-xs text-boss-text-muted">—</span>
        )}
      </td>

      {/* Customer */}
      <td className="px-3 py-2.5 max-w-[220px]">
        <div className="font-medium text-boss-text truncate">{flat.customerName || '—'}</div>
      </td>

      {/* Advisor */}
      <td className="px-3 py-2.5 text-boss-text-secondary text-sm truncate max-w-[160px]">
        {flat.advisorCompany || '—'}
      </td>

      {/* Location */}
      <td className="px-3 py-2.5 text-boss-text-secondary text-sm truncate max-w-[120px]">
        {flat.cityState || '—'}
      </td>

      {/* MRC */}
      <td className="px-3 py-2.5 text-right text-sm font-medium text-boss-text tabular-nums">
        {fmtMoney(flat.mrc)}
      </td>

      {/* Type */}
      <td className="px-3 py-2.5 text-boss-text-secondary text-sm">
        {flat.contractType || '—'}
      </td>

      {/* Confidence */}
      <td className="px-3 py-2.5">
        <ConfidenceBar score={flat.confidenceScore} />
      </td>

      {/* Status */}
      <td className="px-3 py-2.5">
        <StatusPill status={row.status} dense />
      </td>

      {/* Flags */}
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap gap-1">
          {flat.flags.slice(0, 3).map((f) => (
            <FlagChip key={f} flag={f} />
          ))}
          {flat.flags.length > 3 && (
            <span className="text-[10px] text-boss-text-secondary">
              +{flat.flags.length - 3}
            </span>
          )}
        </div>
      </td>

      {/* Chevron */}
      <td className="px-3 py-2.5 w-8">
        <ChevronRight className="h-4 w-4 text-boss-text-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
      </td>
    </tr>
  );
}

// Compact strip showing live-fetch stats when available
function SnapshotStatsStrip({ stats, suppressZeroWarning }) {
  if (!stats) return null;
  const secs = stats.fetch_ms != null ? (stats.fetch_ms / 1000).toFixed(1) : null;
  const hasFailures = Array.isArray(stats.partial_failures) && stats.partial_failures.length > 0;

  // customers_found === 0 means every row in this session will score as
  // unmatched — surface it the same way as the trigger-errors banner below
  // instead of burying it in muted text. Suppressed when the AppDirect
  // auth-failure banner already covers this session (same root cause,
  // no need to say it twice).
  const showZeroWarning = stats.customers_found === 0 && !suppressZeroWarning;

  return (
    <>
      {showZeroWarning && (
        <div
          className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-boss-status-warn-bg border border-[var(--boss-warning-border)] text-boss-status-warn text-xs"
          data-testid="board-zero-candidates-banner"
        >
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
          <span>
            Fetched 0 customer candidates from Provider Sales — every row in
            this session will show as unmatched. Check the provider mapping in
            Settings before reviewing rows.
          </span>
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap text-xs text-boss-text-secondary">
        <span>
          Fetched{' '}
          <strong className="text-boss-text">{stats.customers_found ?? '?'}</strong>{' '}
          customer candidates +{' '}
          <strong className="text-boss-text">{stats.orders_found ?? '?'}</strong>{' '}
          orders from Provider Sales
          {secs && (
            <>
              {' '}in{' '}
              <strong className="text-boss-text">{secs}s</strong>
            </>
          )}
        </span>
        {hasFailures && (
          <details className="cursor-pointer">
            <summary className="inline-flex items-center gap-1 text-boss-status-warn list-none select-none">
              <AlertTriangle className="h-3 w-3" />
              {stats.partial_failures.length} partial failure
              {stats.partial_failures.length > 1 ? 's' : ''}
            </summary>
            <ul className="mt-1 ml-4 space-y-0.5">
              {stats.partial_failures.map((msg, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i} className="text-boss-text-muted">{msg}</li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function BoardTab() {
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  // ?session= is read REACTIVELY, not once at mount. "View on Board" in the
  // Sessions tab navigates to /match/board?session=<id>; a one-shot ref only
  // worked if BoardTab happened to mount after the navigation committed, so
  // repeat navigations (and back/forward) silently kept the old session.
  const sessionParam = searchParams.get('session');

  // Session data
  const [sessions, setSessions]                     = useState([]);
  const [selectedSessionId, setSelectedSessionId]   = useState('');
  const [session, setSession]                       = useState(null);
  const [rows, setRows]                             = useState([]);

  // UI state
  const [search, setSearch]                         = useState('');
  const [flagFilters, setFlagFilters]               = useState([]);
  const [statusFilters, setStatusFilters]           = useState([]);
  const [sortKey, setSortKey]                       = useState('row_index');
  const [sortDir, setSortDir]                       = useState('asc');

  // Loading flags
  const [loadingSessions, setLoadingSessions]       = useState(false);
  const [loadingRows, setLoadingRows]               = useState(false);
  const [bulkBusy, setBulkBusy]                     = useState(false);
  const [simBusy, setSimBusy]                       = useState(false);
  const [rowsError, setRowsError]                   = useState(null);

  // Processing poller timeout flag
  const [processingTimedOut, setProcessingTimedOut] = useState(false);

  // Drawer state
  const [selectedRowId, setSelectedRowId]           = useState(null);

  // Row-selection state for "Approve selected". A Set of row_ids so
  // membership checks are O(1) — the board can carry thousands of rows and
  // per-row array scans in render would be quadratic.
  const [selectedRowIds, setSelectedRowIds]         = useState(() => new Set());

  // Refresh trigger
  const [refreshKey, setRefreshKey]                 = useState(0);

  // Silent-refetch bookkeeping (polling thrash fix, load test 2026-07-10):
  // refreshKey re-runs the fetch effects every 5s while rows stream in. The
  // loading skeletons must only show on FIRST load / session switch — at
  // 1,500 rows the refetch takes >1s, and flipping the loading state on
  // every poll made the whole row list visibly collapse and reappear.
  const sessionsLoadedRef = useRef(false);
  const loadedSessionRef  = useRef(null);
  // Live row count for the poller's stability check — the interval closure
  // must read a ref, not `rows` (a stale closure over rows.length made the
  // stop-counters compare a constant against itself).
  const rowCountRef       = useRef(0);

  // Search debounce
  const debounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch]       = useState('');

  // Permissions
  // Server-side gate is `match.row.change_status` (match_row.py); the
  // `bulk_approve` slug doesn't exist in the catalog. Keep it as a
  // forward-compat fallback in case the slug gets split out later.
  const canBulkApprove = hasPermission('match.row.change_status') || hasPermission('match.row.bulk_approve');

  // ---------------------------------------------------------------------------
  // Search debounce
  // ---------------------------------------------------------------------------

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(val), 250);
  }, []);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // ---------------------------------------------------------------------------
  // Reset processing timeout when session changes
  // ---------------------------------------------------------------------------

  useEffect(() => {
    setProcessingTimedOut(false);
    // Clear hand-picked selection whenever the active session changes — row_ids
    // are session-scoped, so carrying them across sessions is never correct.
    setSelectedRowIds(new Set());
  }, [selectedSessionId]);

  // ---------------------------------------------------------------------------
  // Fetch session list
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let alive = true;
    // Silent on poll refreshes — skeleton only before the first load lands.
    if (!sessionsLoadedRef.current) setLoadingSessions(true);
    api
      .get(`${BASE}/sessions`)
      .then(async (res) => {
        if (!alive) return;
        const data = await res.json();
        if (!res.ok) return;
        const items = unwrap(data) ?? [];
        items.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
        setSessions(items);
        sessionsLoadedRef.current = true;

        // The URL param owns the selection only when it RESOLVES to a session in
        // this list. An earlier revision tested mere presence (`!sessionParam`),
        // which left a third case uncovered — param present but unresolvable:
        // deleted session, stale bookmark, another tenant's id. Neither this
        // guard nor the ?session= sync effect below accepted that input, so
        // selectedSessionId stayed '' forever and the board rendered
        // permanently blank with no self-heal on the 5s poll.
        const paramResolves =
          !!sessionParam && items.some((s) => s.session_id === sessionParam);

        if (!paramResolves && items.length > 0) {
          // Functional update = compare-and-set. This .then resolves long after
          // the request was issued, so the `selectedSessionId` captured in this
          // closure may already be stale; only claim the slot if it is still
          // genuinely empty.
          setSelectedSessionId((cur) => cur || items[0].session_id);
        }

        // Drop the dead param so the URL stops contradicting the board and a
        // refresh doesn't re-enter this branch. Safe because list_sessions
        // returns the tenant's newest 500 in a single response (no pagination —
        // see match_session.py::list_sessions), so "absent from items" means
        // "not reachable by this user", never "on another page". If that
        // endpoint ever gains pagination, revisit this.
        if (sessionParam && !paramResolves) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              // Only clear if the URL still holds the id we judged stale — the
              // operator may have navigated to a valid session while this
              // request was in flight.
              if (next.get('session') === sessionParam) next.delete('session');
              return next;
            },
            { replace: true },
          );
        }
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoadingSessions(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // ---------------------------------------------------------------------------
  // Keep the selected session in sync with ?session= (both directions)
  //
  // URL -> state: honour the param as soon as the named session exists in the
  // list. This is what makes "View on Board" work on every click, not just the
  // first mount.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!sessionParam) return;
    if (sessionParam === selectedSessionId) return;
    if (sessions.some((s) => s.session_id === sessionParam)) {
      setSelectedSessionId(sessionParam);
    }
  }, [sessionParam, sessions, selectedSessionId]);

  // state -> URL: when the operator picks a session from the dropdown, rewrite
  // the param (replace, so the back button doesn't fill up with board states).
  // Without this the effect above would immediately snap the dropdown back.
  const handleSessionChange = useCallback((id) => {
    setSelectedSessionId(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('session', id);
        else next.delete('session');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // ---------------------------------------------------------------------------
  // Fetch session detail + rows when session changes
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!selectedSessionId) {
      setSession(null);
      setRows([]);
      setRowsError(null);
      return;
    }
    let alive = true;
    // Skeleton only when switching sessions — poll refreshes swap the data
    // in place so the rendered list (and any expanded rows) never unmounts.
    const isSessionSwitch = loadedSessionRef.current !== selectedSessionId;
    if (isSessionSwitch) setLoadingRows(true);
    setRowsError(null);

    Promise.all([
      api.get(`${BASE}/sessions/${selectedSessionId}`).then((r) => r.json()),
      // slim=true drops source_row / candidate shortlists / escalation drafts
      // (3.6MB -> well under 1MB at 1,500 rows). The detail drawer fetches
      // its own FULL row by id — the list only feeds flattenRow.
      api.get(`${BASE}/rows/by-session/${selectedSessionId}?slim=true`).then((r) => r.json()),
    ])
      .then(([sessData, rowsData]) => {
        if (!alive) return;
        setSession(sessData?.data ?? sessData ?? null);
        setRows(unwrap(rowsData) ?? []);
        loadedSessionRef.current = selectedSessionId;
      })
      .catch((err) => {
        if (!alive) return;
        setRowsError(err?.message || 'Failed to load rows.');
        setRows([]);
      })
      .finally(() => { if (alive) setLoadingRows(false); });

    return () => { alive = false; };
  }, [selectedSessionId, refreshKey]);

  // ---------------------------------------------------------------------------
  // Auto-refresh while n8n rows are still streaming in
  //
  // Polls every 5s while session.status is 'processing' OR 'processed'.
  // 'processed' must keep polling too: the backend flips the session to
  // 'processed' when the FIRST row-processed callback lands (rows arriving is
  // the only completion signal until n8n sends total_rows), so gating on
  // 'processing' alone tore the poll down after row 1 and rows 2..N never
  // appeared without a manual refresh. Stops on any of:
  //   - 36 ticks elapsed (~3 min) — sets processingTimedOut for a "still
  //     waiting" hint; operator can manually refresh
  //   - row count is stable across 5 consecutive polls (n8n is done or stuck)
  //   - component unmount / session change
  // ---------------------------------------------------------------------------
  useEffect(() => { rowCountRef.current = rows.length; }, [rows.length]);

  useEffect(() => {
    if (!selectedSessionId) return;
    if (session?.status !== 'processing' && session?.status !== 'processed') return;

    let alive = true;
    let ticks = 0;
    let stableCount = 0;
    let lastRowCount = rowCountRef.current;

    const intervalId = setInterval(() => {
      if (!alive) return;
      ticks += 1;
      if (ticks > 36) {
        clearInterval(intervalId);
        setProcessingTimedOut(true);
        return;
      }
      setRefreshKey((k) => k + 1);
      // rowCountRef (not `rows`): the interval closure would otherwise
      // compare a stale snapshot of rows.length against itself forever, and
      // putting rows.length in the effect deps re-armed the interval (and
      // reset these counters) on every single row that streamed in.
      if (rowCountRef.current === lastRowCount) {
        stableCount += 1;
        if (stableCount >= 5) {
          clearInterval(intervalId);
          return;
        }
      } else {
        stableCount = 0;
        lastRowCount = rowCountRef.current;
      }
    }, 5000);

    return () => {
      alive = false;
      clearInterval(intervalId);
    };
  }, [selectedSessionId, session?.status]);
  // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Computed: status counters
  // ---------------------------------------------------------------------------

  const counters = useMemo(() => {
    const c = { ready_to_draft: 0, needs_review: 0, approved: 0, rejected: 0, escalated: 0 };
    for (const r of rows) {
      if (c[r.status] != null) c[r.status]++;
      else if (r.recommended_status && c[r.recommended_status] != null) c[r.recommended_status]++;
    }
    return c;
  }, [rows]);

  // ---------------------------------------------------------------------------
  // Computed: eligible for bulk approve (score >= 0.95 and actionable status)
  // ---------------------------------------------------------------------------

  const eligibleRows = useMemo(() =>
    rows.filter((r) => {
      const flat = flattenRow(r);
      return flat.confidenceScore >= 95 &&
        ['ready_to_draft', 'needs_review'].includes(r.status);
    }),
    [rows]
  );

  // ---------------------------------------------------------------------------
  // Computed: flags present in current row set
  // ---------------------------------------------------------------------------

  const presentFlags = useMemo(() => {
    const set = new Set();
    for (const r of rows) {
      const f = flattenRow(r).flags;
      f.forEach((flag) => set.add(flag));
    }
    return FLAG_FILTER_DEFS.filter((d) => set.has(d.key));
  }, [rows]);

  // ---------------------------------------------------------------------------
  // Computed: filtered + sorted rows
  // ---------------------------------------------------------------------------

  const filteredRows = useMemo(() => {
    let out = rows.slice();

    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      out = out.filter((r) => {
        const flat = flattenRow(r);
        return (
          flat.customerName.toLowerCase().includes(q) ||
          flat.advisorCompany.toLowerCase().includes(q) ||
          (r.row_id || '').toLowerCase().includes(q) ||
          (flat.psOrderId != null && String(flat.psOrderId).includes(q))
        );
      });
    }

    if (statusFilters.length > 0) {
      out = out.filter((r) => statusFilters.includes(r.status));
    }

    if (flagFilters.length > 0) {
      out = out.filter((r) =>
        flattenRow(r).flags.some((f) => flagFilters.includes(f))
      );
    }

    out.sort((a, b) => {
      let av, bv;
      const flatA = flattenRow(a);
      const flatB = flattenRow(b);
      switch (sortKey) {
        case 'confidence':
          av = flatA.confidenceScore;
          bv = flatB.confidenceScore;
          break;
        case 'customer':
          av = flatA.customerName.toLowerCase();
          bv = flatB.customerName.toLowerCase();
          break;
        case 'advisor':
          av = flatA.advisorCompany.toLowerCase();
          bv = flatB.advisorCompany.toLowerCase();
          break;
        case 'mrc':
          av = flatA.mrc ?? 0;
          bv = flatB.mrc ?? 0;
          break;
        case 'status':
          av = a.status ?? '';
          bv = b.status ?? '';
          break;
        default:
          av = a.row_index ?? 0;
          bv = b.row_index ?? 0;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return out;
  }, [rows, debouncedSearch, statusFilters, flagFilters, sortKey, sortDir]);

  // ---------------------------------------------------------------------------
  // Client-side pagination — the board rendered EVERY filtered row in one
  // list, which at 1,500 rows meant 1,500 mounted row components re-rendering
  // on each 5s poll refresh (load test 2026-07-10). Counters, eligibility,
  // and search still operate on the FULL set; only the render is paged.
  // ---------------------------------------------------------------------------

  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / BOARD_PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages - 1);
  const pagedRows = useMemo(
    () => filteredRows.slice(clampedPage * BOARD_PAGE_SIZE, (clampedPage + 1) * BOARD_PAGE_SIZE),
    [filteredRows, clampedPage]
  );

  // New search/filter/sort/session almost always changes what page N means —
  // snap back to the first page rather than strand the user on an empty one.
  useEffect(() => { setPage(0); }, [debouncedSearch, statusFilters, flagFilters, sortKey, sortDir, selectedSessionId]);

  // ---------------------------------------------------------------------------
  // Computed: selection over the currently-VISIBLE (current page) rows
  //
  // "Select all" operates on the rows the user can actually SEE — the current
  // page of the filtered set. Ineligible-status rows are excluded from both
  // the select-all target and the header checkbox's all/indeterminate math.
  // ---------------------------------------------------------------------------

  const selectableVisibleIds = useMemo(
    () => pagedRows.filter(isRowSelectable).map((r) => r.row_id),
    [pagedRows]
  );

  const selectedCount = selectedRowIds.size;

  // Header checkbox state derived from the visible-selectable set vs. the
  // selection Set. allSelected drives `checked`; the partial case drives the
  // indeterminate dash (set via ref below).
  const allVisibleSelected =
    selectableVisibleIds.length > 0 &&
    selectableVisibleIds.every((id) => selectedRowIds.has(id));
  const someVisibleSelected =
    !allVisibleSelected && selectableVisibleIds.some((id) => selectedRowIds.has(id));

  const selectAllRef = useRef(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  const handleSort = useCallback((key) => {
    setSortKey(key);
    setSortDir((cur) => (sortKey === key ? (cur === 'asc' ? 'desc' : 'asc') : 'asc'));
  }, [sortKey]);

  const toggleFlagFilter = useCallback((key) => {
    setFlagFilters((cur) =>
      cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key]
    );
  }, []);

  const toggleStatusFilter = useCallback((key) => {
    setStatusFilters((cur) =>
      cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key]
    );
  }, []);

  const clearFilters = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    setFlagFilters([]);
    setStatusFilters([]);
  }, []);

  const hasActiveFilters = search || flagFilters.length > 0 || statusFilters.length > 0;

  const handleRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  // "Re-run simulate" maps to /process — re-kicks the n8n matching
  // workflow for the existing session. The source POC had a separate
  // /simulate endpoint that loaded fixtures; we didn't ingest it, so
  // this just re-fires the same n8n trigger SessionsTab uses on create.
  const handleSimulate = useCallback(async () => {
    if (!selectedSessionId) return;
    setSimBusy(true);
    try {
      const res = await api.post(`${BASE}/sessions/${selectedSessionId}/process`, {});
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || 'Re-run failed');
      } else {
        toast.success('Processing queued — rows arriving shortly');
        setRefreshKey((k) => k + 1);
      }
    } catch {
      toast.error('Re-run failed');
    } finally {
      setSimBusy(false);
    }
  }, [selectedSessionId]);

  const handleBulkApprove = useCallback(async () => {
    if (!selectedSessionId) return;
    if (eligibleRows.length === 0) {
      toast.info('No rows with confidence ≥ 95% to bulk-approve.');
      return;
    }
    setBulkBusy(true);
    try {
      const res = await api.post(`${BASE}/rows/bulk-approve`, {
        session_id: selectedSessionId,
        threshold: 95,
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || 'Bulk approve failed');
      } else if (data?.data?.background ?? data?.background) {
        // Big sweeps run server-side in the background (a 1,194-row sweep held
        // the request open for 78s). Statuses flip over the next minute or so;
        // schedule a few silent refreshes to pick them up as they land.
        const accepted = data?.data?.accepted_count ?? data?.accepted_count ?? eligibleRows.length;
        toast.success(`Approving ${accepted} rows in the background — statuses will update as they complete`);
        for (const delay of [8000, 20000, 40000, 70000, 110000]) {
          setTimeout(() => setRefreshKey((k) => k + 1), delay);
        }
      } else {
        const count = data?.data?.approved_count ?? data?.approved_count ?? eligibleRows.length;
        toast.success(`Bulk-approved ${count} rows`);
        setRefreshKey((k) => k + 1);
      }
    } catch {
      toast.error('Bulk approve failed');
    } finally {
      setBulkBusy(false);
    }
  }, [selectedSessionId, eligibleRows]);

  const toggleRowSelected = useCallback((rowId) => {
    setSelectedRowIds((cur) => {
      const next = new Set(cur);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  // Select-all toggles the VISIBLE selectable rows only. If they're all
  // already selected, it deselects them (leaving any off-screen selections
  // that a prior filter view added — clearing those would be surprising).
  const toggleSelectAllVisible = useCallback(() => {
    setSelectedRowIds((cur) => {
      const next = new Set(cur);
      const allOn = selectableVisibleIds.length > 0 &&
        selectableVisibleIds.every((id) => next.has(id));
      if (allOn) {
        selectableVisibleIds.forEach((id) => next.delete(id));
      } else {
        selectableVisibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [selectableVisibleIds]);

  const handleApproveSelected = useCallback(async () => {
    if (!selectedSessionId) return;
    const ids = Array.from(selectedRowIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.post(`${BASE}/rows/bulk-approve`, {
        session_id: selectedSessionId,
        row_ids: ids,
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || 'Approve selected failed');
      } else {
        const count = data?.data?.approved_count ?? data?.approved_count ?? 0;
        toast.success(`Approved ${count} selected row${count === 1 ? '' : 's'}`);
        setSelectedRowIds(new Set());
        setRefreshKey((k) => k + 1);
      }
    } catch {
      toast.error('Approve selected failed');
    } finally {
      setBulkBusy(false);
    }
  }, [selectedSessionId, selectedRowIds]);

  const handleRowActioned = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const isProcessing = session?.status === 'processing';
  const snapshotStats = session?.snapshot_stats ?? null;
  const snapshotSourceCls = SNAPSHOT_SOURCE_CLS[session?.snapshot_source] || '';
  const snapshotSourceLabel = SNAPSHOT_SOURCE_LABEL[session?.snapshot_source] || null;

  return (
    <div className="space-y-4" data-testid="match-board-tab">

      {/* ── Session selector + Header ── */}
      <div className="glass-card p-4 space-y-3">
        {/* Session selector row */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex flex-col gap-1 min-w-[240px] max-w-[360px] flex-1">
            <label className="text-xs font-medium text-boss-text-secondary">Session</label>
            <select
              className="boss-input px-2 py-1.5 text-sm rounded-md border border-boss-border bg-boss-surface text-boss-text"
              value={selectedSessionId}
              onChange={(e) => handleSessionChange(e.target.value)}
              disabled={loadingSessions}
              data-testid="board-session-select"
            >
              {loadingSessions && <option value="">Loading sessions…</option>}
              {!loadingSessions && sessions.length === 0 && (
                <option value="">No sessions available</option>
              )}
              {!loadingSessions && sessions.length > 0 && (
                <>
                  <option value="">— Select session —</option>
                  {sessions.map((s) => (
                    <option key={s.session_id} value={s.session_id}>
                      {s.label || s.session_id}
                      {s.provider ? ` (${s.provider})` : ''}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              data-testid="board-refresh-btn"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Refresh
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleSimulate}
              disabled={simBusy || !selectedSessionId}
              data-testid="board-simulate-btn"
            >
              {simBusy ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5 mr-1" />
              )}
              Re-run simulate
            </Button>
          </div>
        </div>

        {/* Session header info */}
        {session && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-boss-text leading-tight">
                {session.label || selectedSessionId}
              </h2>
              {isProcessing && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-boss-status-info-bg text-boss-status-info border-[var(--boss-info-border)]">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Processing
                </span>
              )}
              {session.status === 'processed' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-boss-status-success-bg text-boss-status-success border-[var(--boss-success-border)]">
                  <CheckCircle2 className="h-3 w-3" />
                  Processed
                </span>
              )}
              {session.status === 'trigger_failed' && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-boss-status-danger-bg text-boss-status-danger border-[var(--boss-danger-border)]">
                  Trigger failed
                </span>
              )}
              {snapshotSourceLabel && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${snapshotSourceCls}`}>
                  {snapshotSourceLabel}
                </span>
              )}
            </div>
            <p className="text-xs text-boss-text-secondary">
              {session.provider ?? '—'} ·{' '}
              {session.status === 'processing' && session.total_rows
                ? `${rows.length} of ${session.total_rows} rows received`
                : `${rows.length} rows received`}
              <SanitizeStatsBadge stats={session.sanitize_stats} />
            </p>
            {/* AppDirect session cookie rejected mid-run: the root cause of a
                zero-candidate fetch, not just a symptom of one — rank it
                above the stats strip and the trigger-errors banner below. */}
            {session.appdirect_auth_failed && (
              <div
                className="flex items-start gap-2 px-3 py-2 rounded-lg bg-boss-status-danger-bg border border-[var(--boss-danger-border)] text-boss-status-danger text-xs"
                data-testid="board-appdirect-auth-banner"
              >
                <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                <span>
                  AppDirect rejected our session cookie while processing this
                  session — the matching results and product lists below are
                  not reliable. Re-authenticate in Settings → AppDirect, then
                  re-run this session.
                  {session.appdirect_auth_message && (
                    <span className="text-boss-text-muted">
                      {' '}({session.appdirect_auth_message})
                    </span>
                  )}
                </span>
              </div>
            )}
            {/* Dispatch refused before n8n was called — the snapshot came back
                empty AND something failed to explain it (rejected cookie or
                partial fetch failures). Without this the session shows only a
                "Trigger failed" badge and the operator has no idea why, which
                is the exact silence this whole wave exists to remove. Ranked
                below the auth banner: when a rejected cookie caused the block,
                that banner already names the root cause and this one repeats
                the fix. */}
            {session.trigger_failed_reason && (
              <div
                className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-boss-status-danger-bg border border-[var(--boss-danger-border)] text-boss-status-danger text-xs"
                data-testid="board-trigger-failed-reason-banner"
              >
                <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                <span>{session.trigger_failed_reason}</span>
              </div>
            )}
            {/* Snapshot fetch stats — live_api mode only */}
            <SnapshotStatsStrip
              stats={snapshotStats}
              suppressZeroWarning={session.appdirect_auth_failed === true}
            />
            {/* Partial dispatch: a mid-run chunk trigger failed, so some rows
                were never sent to n8n. Without this banner the session reads
                "Processed" and a partial failure is indistinguishable from
                full success (total_rows was shrunk to what was dispatched so
                the completion signal could resolve). */}
            {Array.isArray(session.trigger_errors) && session.trigger_errors.length > 0 && (
              <div
                className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-boss-status-warn-bg border border-[var(--boss-warning-border)] text-boss-status-warn text-xs"
                data-testid="board-trigger-errors-banner"
              >
                <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                <span>
                  Partial import: some rows were never dispatched to matching —{' '}
                  only {session.total_rows ?? '?'} of the report&apos;s rows were sent.
                  Re-import the report to retry.{' '}
                  <span className="text-boss-text-muted">
                    ({session.trigger_errors.join('; ')})
                  </span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Stat cards ── */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2"
        data-testid="board-stat-cards"
      >
        {STAT_CARDS.map((card) => (
          <StatCard
            key={card.key}
            label={card.label}
            value={counters[card.key] ?? 0}
            accent={card.accent}
            active={statusFilters.includes(card.key)}
            onClick={() => toggleStatusFilter(card.key)}
            testId={`board-stat-${card.key}`}
          />
        ))}
      </div>

      {/* ── Filter strip ── */}
      <div className="flex flex-wrap items-center gap-2" data-testid="board-filter-strip">
        {/* Search */}
        <div className="relative flex-1 min-w-[240px] max-w-[380px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-boss-text-secondary pointer-events-none" />
          <Input
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by PS order, customer, advisor, ID…"
            className="input-field h-9 pl-8"
            data-testid="board-search-input"
          />
        </div>

        {/* Flag chips */}
        {presentFlags.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="board-flag-filters">
            <span className="text-xs text-boss-text-secondary inline-flex items-center gap-1">
              <Filter className="h-3 w-3" />
              Flags:
            </span>
            {presentFlags.map((def) => {
              const on = flagFilters.includes(def.key);
              return (
                <button
                  key={def.key}
                  type="button"
                  onClick={() => toggleFlagFilter(def.key)}
                  className={[
                    'inline-flex items-center rounded-full border px-2 py-0.5 text-xs transition-colors',
                    on
                      ? 'border-boss-border bg-boss-status-info-bg text-boss-status-info'
                      : 'border-boss-border bg-boss-surface text-boss-text-secondary hover:bg-boss-surface-elevated hover:text-boss-text',
                  ].join(' ')}
                  data-testid={`board-flag-filter-${def.key}`}
                >
                  {def.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Clear all */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center gap-1 text-xs text-boss-text-secondary hover:text-boss-text transition-colors"
            data-testid="board-clear-filters"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        )}

        {/* Bulk approve */}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-boss-text-secondary">
            {eligibleRows.length} eligible
          </span>
          {canBulkApprove && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleApproveSelected}
                disabled={bulkBusy || selectedCount === 0 || !selectedSessionId}
                data-testid="board-approve-selected-btn"
              >
                {bulkBusy ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                )}
                Approve selected ({selectedCount})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkApprove}
                disabled={bulkBusy || eligibleRows.length === 0 || !selectedSessionId}
                data-testid="board-bulk-approve-btn"
              >
                {bulkBusy ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <CheckCheck className="h-3.5 w-3.5 mr-1" />
                )}
                Bulk approve ≥ 95%
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ── Data table ──
          min-h keeps the card from collapsing when a status filter narrows the
          set to a handful of rows; without it the page height changes on every
          filter toggle and the browser's scroll anchor makes the list "jump". */}
      <div className="glass-card overflow-hidden min-h-[420px]" data-testid="board-table-container">
        {!selectedSessionId ? (
          <div className="py-14 text-center text-boss-text-secondary text-sm">
            Select a session above to view rows.
          </div>
        ) : rowsError ? (
          <div className="py-10 px-4 text-center text-boss-status-danger text-sm">
            {rowsError}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="board-rows-table">
              <thead>
                <tr className="border-b border-boss-border bg-boss-surface-elevated">
                  {/* Select-all-visible checkbox */}
                  <th className="px-3 py-2 w-8">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      className="h-4 w-4 rounded border-boss-border bg-boss-surface accent-[var(--boss-accent)] cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                      checked={allVisibleSelected}
                      disabled={selectableVisibleIds.length === 0}
                      onChange={toggleSelectAllVisible}
                      title="Select all visible approvable rows"
                      data-testid="board-select-all"
                    />
                  </th>
                  {/* PS Order — first column, most important identifier per stakeholder */}
                  <th className="text-left px-3 py-2 text-xs font-medium text-boss-text-secondary whitespace-nowrap w-[110px]">
                    PS Order
                  </th>
                  <SortableTh
                    label="Customer"
                    sortKey="customer"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Advisor"
                    sortKey="advisor"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <th className="text-left px-3 py-2 text-xs font-medium text-boss-text-secondary">
                    Location
                  </th>
                  <SortableTh
                    label="MRC"
                    sortKey="mrc"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <th className="text-left px-3 py-2 text-xs font-medium text-boss-text-secondary">
                    Type
                  </th>
                  <SortableTh
                    label="Confidence"
                    sortKey="confidence"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <th className="text-left px-3 py-2 text-xs font-medium text-boss-text-secondary">
                    Flags
                  </th>
                  <th className="w-8 px-3" />
                </tr>
              </thead>
              <tbody>
                {loadingRows ? (
                  <SkeletonRows />
                ) : filteredRows.length === 0 ? (
                  <EmptyState
                    hasRows={rows.length > 0}
                    onSimulate={rows.length === 0 ? handleSimulate : null}
                    processing={isProcessing}
                    timedOut={processingTimedOut}
                  />
                ) : (
                  pagedRows.map((row) => (
                    <BoardRow
                      key={row.row_id}
                      row={row}
                      selected={selectedRowId === row.row_id}
                      checked={selectedRowIds.has(row.row_id)}
                      selectable={isRowSelectable(row)}
                      onToggleSelect={toggleRowSelected}
                      onClick={() => setSelectedRowId(row.row_id)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pager — only when the filtered set exceeds one page */}
        {filteredRows.length > BOARD_PAGE_SIZE && (
          <div
            className="flex items-center justify-between px-4 py-2.5 border-t border-boss-border text-xs text-boss-text-secondary"
            data-testid="board-pager"
          >
            <span>
              Showing {clampedPage * BOARD_PAGE_SIZE + 1}–
              {Math.min((clampedPage + 1) * BOARD_PAGE_SIZE, filteredRows.length)} of{' '}
              {filteredRows.length} rows
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-2.5 py-1 rounded border border-boss-border bg-boss-elevated hover:bg-boss-surface disabled:opacity-40 disabled:cursor-not-allowed transition"
                onClick={() => setPage(clampedPage - 1)}
                disabled={clampedPage === 0}
                data-testid="board-pager-prev"
              >
                Prev
              </button>
              <span className="text-boss-text-muted">
                Page {clampedPage + 1} of {totalPages}
              </span>
              <button
                type="button"
                className="px-2.5 py-1 rounded border border-boss-border bg-boss-elevated hover:bg-boss-surface disabled:opacity-40 disabled:cursor-not-allowed transition"
                onClick={() => setPage(clampedPage + 1)}
                disabled={clampedPage >= totalPages - 1}
                data-testid="board-pager-next"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Row detail drawer ── */}
      <RowDetailDrawer
        rowId={selectedRowId}
        open={!!selectedRowId}
        onOpenChange={(v) => { if (!v) setSelectedRowId(null); }}
        onActioned={handleRowActioned}
      />
    </div>
  );
}
